<!--
  Gmail-specific success illustration shown inside the wizard after a
  successful connection. Receives the selected filter choice and name from
  the parent workflow component.

  choice === "optionA" → starred-email mode (star animation)
  choice === "optionB" → label mode (label chip overlay)
-->
<template>
  <div class="gmail-illus" :class="{ 'is-starred': choice === 'optionA' }">
    <div class="inbox-preview">
      <div class="inbox-row unread">
        <span class="inbox-dot active" />
        <span class="inbox-star">☆</span>
        <span class="inbox-avatar" style="background: #e8f0fe; color: #1a73e8"
          >SL</span
        >
        <span class="inbox-name">Sarah Lecomte</span>
        <span class="inbox-subject">Designs for v1.0 landing page</span>
        <span class="inbox-preview-text"> — Hey, here are the…</span>
      </div>
      <div class="inbox-row">
        <span class="inbox-dot" />
        <span class="inbox-star first-star">{{
          choice === "optionA" ? "★" : "☆"
        }}</span>
        <span class="inbox-avatar" style="background: #e6f4ea; color: #188038"
          >TN</span
        >
        <span class="inbox-name">Thomas Nguyen</span>
        <span class="inbox-subject">Re: Video shoot — location co…</span>
        <span class="inbox-preview-text"> — Perfect, the st…</span>
      </div>
      <div class="inbox-row unread">
        <span class="inbox-dot active" />
        <span class="inbox-star">★</span>
        <span class="inbox-avatar" style="background: #fce8e6; color: #c5221f"
          >AM</span
        >
        <span class="inbox-name">Alexandre Moreau</span>
        <span class="inbox-subject">Investor update — June</span>
        <span class="inbox-preview-text"> — Happy to share that MR…</span>
      </div>
      <div class="inbox-row">
        <span class="inbox-dot" />
        <span class="inbox-star">☆</span>
        <span class="inbox-avatar" style="background: #f3e8fd; color: #8430ce"
          >CC</span
        >
        <span class="inbox-name">Clara Chevalier</span>
        <span class="inbox-subject">Freelance contract — signatur…</span>
        <span class="inbox-preview-text"> — Hi, I've attache…</span>
      </div>
      <div class="inbox-row">
        <span class="inbox-dot" />
        <span class="inbox-star">☆</span>
        <span class="inbox-avatar" style="background: #e6f4ea; color: #137333"
          >LB</span
        >
        <span class="inbox-name">Lena Baumann</span>
        <span class="inbox-subject">Partnership proposal — Routin…</span>
        <span class="inbox-preview-text"> — I work on the e…</span>
      </div>
    </div>

    <div v-if="choice === 'optionB'" class="inbox-label-overlay">
      <span class="inbox-label-icon">🏷</span>
      <span>Add {{ filterName }}</span>
    </div>
  </div>

  <p class="success-caption">
    <template v-if="choice === 'optionA'">
      Star an email in Gmail and it will become a task in your Routine inbox.
    </template>
    <template v-else>
      Emails labelled <strong>{{ filterName }}</strong> in Gmail will
      automatically become tasks in your Inbox in Routine.
    </template>
  </p>
</template>

<script lang="ts">
import { defineComponent } from "vue";

export default defineComponent({
  props: {
    choice: { type: String, required: true },
    filterName: { type: String, default: "" },
  },
});
</script>

<style scoped lang="scss">
@use "../../../styles/settings.scss";

.gmail-illus {
  flex: 1;
  border-radius: $border-radius-m;
  overflow: hidden;
  position: relative;
  min-height: 160px;
  @include themed() {
    border: 1px solid t($grey-10);
  }

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    pointer-events: none;
    @include themed() {
      background: linear-gradient(to bottom, transparent 40%, t($bg) 100%);
    }
  }
}

.inbox-preview {
  display: flex;
  flex-direction: column;
  opacity: 0.78;
  pointer-events: none;
  user-select: none;
}

.gmail-illus.is-starred .inbox-preview,
.gmail-illus:has(.inbox-label-overlay) .inbox-preview {
  opacity: 1;
}

.inbox-label-overlay {
  position: absolute;
  top: 80px;
  inset-inline-end: 14px;
  transform: translateY(-50%) translateY(-20px);
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 6px 10px;
  border-radius: $border-radius-m;
  @extend %font-caption-regular;
  pointer-events: none;
  box-shadow:
    0 0 0 0 rgba(80, 136, 110, 0.5),
    0 2px 8px rgba(0, 0, 0, 0.12);
  animation: label-pulse 2s ease-out infinite;
  @include themed() {
    background: t($bg);
    color: t($grey-80);
    border: 1px solid t($grey-30);
  }
}

.inbox-label-icon {
  font-size: 12px;
  line-height: 1;
}

@keyframes label-pulse {
  0% {
    box-shadow:
      0 0 0 0 rgba(80, 136, 110, 0.55),
      0 2px 8px rgba(0, 0, 0, 0.12);
  }
  60% {
    box-shadow:
      0 0 0 10px rgba(80, 136, 110, 0),
      0 2px 8px rgba(0, 0, 0, 0.12);
  }
  100% {
    box-shadow:
      0 0 0 0 rgba(80, 136, 110, 0),
      0 2px 8px rgba(0, 0, 0, 0.12);
  }
}

.inbox-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 14px;
  height: 40px;
  font-size: 11px;
  @include themed() {
    border-bottom: 1px solid t($grey-05);
  }

  &.unread {
    @include themed() {
      background: t($grey-03);
    }

    .inbox-name,
    .inbox-subject {
      font-weight: 600;
    }
  }
}

.inbox-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;

  &.active {
    background: #1a73e8;
  }
}

.inbox-star {
  font-size: 13px;
  flex-shrink: 0;
  line-height: 1;
  @include themed() {
    color: t($grey-30);
  }
}

.gmail-illus.is-starred .first-star {
  font-size: 15px;
  position: relative;
  z-index: 1;
  display: inline-flex;
  filter: drop-shadow(0 0 8px rgba(251, 188, 4, 1));
  @include themed() {
    color: #f9ab00;
  }

  &::before,
  &::after {
    content: "";
    position: absolute;
    inset: -4px;
    border-radius: 50%;
    background: rgba(249, 171, 0, 0.45);
    animation: star-pulse 2s ease-out infinite;
    pointer-events: none;
  }

  &::after {
    animation-delay: 1s;
  }
}

@keyframes star-pulse {
  0% {
    transform: scale(1);
    opacity: 0.5;
  }
  100% {
    transform: scale(5);
    opacity: 0;
  }
}

.inbox-avatar {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  flex-shrink: 0;
  font-size: 9px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
}

.inbox-name {
  width: 110px;
  flex-shrink: 0;
  white-space: nowrap;
  overflow: hidden;
  @include themed() {
    color: t($primary);
  }
}

.inbox-subject {
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  @include themed() {
    color: t($primary);
  }
}

.inbox-preview-text {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 120px;
  flex-shrink: 0;
  @include themed() {
    color: t($grey-50);
  }
}

.success-caption {
  @extend %font-body-regular;
  margin: 0;
  @include themed() {
    color: t($grey-50);
  }
}
</style>
